import { ENUM__error_type } from '@shared/asserts/ENUM__error_type';
import { assert_equal, assert_string_not_empty, assert_type_array, assert_type_boolean, assert_type_enum } from '@shared/asserts/utils_asserts';
import { assert_string_uuid } from '@shared/asserts/utils_asserts_string';
import { DTO__base } from '@shared/common/DTO__base';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { CALLBACK_FILTER__graph_edge } from '@shared/types/types';
import { ENUM__field_kind } from '../_enums/ENUM__field_kind';
import { ERROR_GRAPH__base } from '../_errors/ERROR_GRAPH__base';
import { ERROR_GRAPH__edge } from '../_errors/ERROR_GRAPH__edge';
import { FIELD__base } from '../_fields/_base/FIELD__base';
import { INVOCATION__base } from '../_invocations/_base/INVOCATION__base';
import { INTERFACE_XYFLOW__edge } from '../_xyflow/INTERFACE_XYFLOW__edge';
import { DTO_GRAPH__instance } from './DTO_GRAPH__instance';

export class DTO_GRAPH__edge extends DTO__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static = ENUM__dto_class_name.GRAPH_EDGE;

  /*
   * STATIC METHODS
   */

  static create(field_from__node_id: string, field_from__field_id: string, field_to__node_id: string, field_to__field_id: string): DTO_GRAPH__edge {
    const out = new DTO_GRAPH__edge();

    [out.field_from__node_id, out.field_from__field_id] = [field_from__node_id, field_from__field_id];
    [out.field_to__node_id, out.field_to__field_id] = [field_to__node_id, field_to__field_id];

    return out;
  }

  static filter_by_id_from_feld(field_from__field_id: string): CALLBACK_FILTER__graph_edge {
    return function (edge: DTO_GRAPH__edge): boolean {
      return edge.field_from__field_id === field_from__field_id;
    };
  }

  static filter_by_id_to_feld(field_to__field_id: string): CALLBACK_FILTER__graph_edge {
    return function (edge: DTO_GRAPH__edge): boolean {
      return edge.field_to__field_id === field_to__field_id;
    };
  }

  /*
   * FIELDS
   */

  field_from__node_name!: string; //names need for debug
  field_from__node_id!: string;

  field_from__field_name!: string; //names need for debug
  field_from__field_id!: string;
  field_from__field_kind!: ENUM__field_kind;

  /*
   *
   */

  field_to__node_name!: string; //names need for debug
  field_to__node_id!: string;

  field_to__field_name!: string; //names need for debug
  field_to__field_id!: string;
  field_to__field_kind!: ENUM__field_kind;

  edge_alias!: string;

  ui__is_visible!: boolean;

  errors!: ERROR_GRAPH__base[];
  is_validated!: boolean;

  /*
   * PROPERTIES
   */

  get sort_criteria() {
    return this.edge_alias.is_blank() ? this.field_to__field_name : this.edge_alias;
  }

  get edge_id() {
    return `${this.toString()}|${this.field_from__field_id}→${this.field_to__field_id}`;
  }

  get_path_from() {
    return `${this.field_from__node_name}.${this.field_from__field_name}`;
  }
  get_path_to() {
    return `${this.field_to__node_name}.${this.field_to__field_name}`;
  }

  /*
   * METHODS
   */

  constructor() {
    super();

    this.edge_alias = '';

    this.errors_clear();

    this.ui__is_visible = false;
  }

  errors_clear() {
    this.errors = [];
    this.is_validated = false;
  }

  get_nodes(graph: DTO_GRAPH__instance): [INVOCATION__base, INVOCATION__base] {
    return [graph.node__get(this.field_from__node_id), graph.node__get(this.field_to__node_id)];
  }

  get_fields(graph: DTO_GRAPH__instance): [FIELD__base, FIELD__base] {
    let [node_from, node_to] = this.get_nodes(graph);

    return [node_from.field__get(this.field_from__field_id), node_to.field__get(this.field_to__field_id)];
  }

  is_equals(other: object): boolean {
    if (!(other instanceof DTO_GRAPH__edge)) return false;

    if (this.field_from__field_id != other.field_from__field_id) return false;
    if (this.field_to__field_id != other.field_to__field_id) return false;
    if (this.field_from__node_id != other.field_from__node_id) return false;
    if (this.field_to__node_id != other.field_to__node_id) return false;

    assert_equal(this.field_from__field_kind, other.field_from__field_kind);
    assert_equal(this.field_to__field_kind, other.field_to__field_kind);

    return true;
  }

  create_reversed(): DTO_GRAPH__edge {
    let out = new DTO_GRAPH__edge();

    [out.field_from__node_id, out.field_from__field_id] = [this.field_to__node_id, this.field_to__field_id];
    [out.field_to__node_id, out.field_to__field_id] = [this.field_from__node_id, this.field_from__field_id];

    [out.field_from__node_name, out.field_from__field_name] = [this.field_to__node_name, this.field_to__field_name];
    [out.field_to__node_name, out.field_to__field_name] = [this.field_from__node_name, this.field_from__field_name];

    [out.field_from__field_kind, out.field_to__field_kind] = [this.field_to__field_kind, this.field_from__field_kind];

    return out;
  }

  toString(): string {
    return `${this.get_path_from()} → ${this.get_path_to()}`;
  }

  to_xyflow_format(selected_edge_id: string): INTERFACE_XYFLOW__edge {
    let [sourceHandle, targetHandle] = [`${this.field_from__field_id}_right`, `${this.field_to__field_id}_left`];

    if (this.field_to__field_kind == ENUM__field_kind.OUTPUT) [sourceHandle, targetHandle] = [targetHandle, sourceHandle];

    let animated = selected_edge_id == this.edge_id;

    let out: INTERFACE_XYFLOW__edge = {
      id: this.edge_id,

      source: this.field_from__node_id,
      target: this.field_to__node_id,
      //
      sourceHandle: `${this.field_from__field_id}_right`,
      targetHandle: `${this.field_to__field_id}_left`,
      //
      type: 'default',
      animated,
      data: this,
    };

    return out;
  }

  error_add(error_type: ENUM__error_type) {
    let out = ERROR_GRAPH__edge.create(error_type, this);

    this.errors.push(out);

    return out;
  }

  /*
   * ASSERTS
   */

  assert_standard_fields(): void {
    assert_string_uuid(this.field_from__node_id);
    assert_string_uuid(this.field_from__field_id);

    assert_string_uuid(this.field_to__node_id);
    assert_string_uuid(this.field_to__field_id);

    assert_string_not_empty(this.field_from__node_name);
    assert_string_not_empty(this.field_from__field_name);

    assert_string_not_empty(this.field_to__node_name);
    assert_string_not_empty(this.field_to__field_name);

    assert_type_enum(this.field_from__field_kind, ENUM__field_kind);
    assert_type_enum(this.field_to__field_kind, ENUM__field_kind);

    assert_type_array(this.errors);
    assert_type_boolean(this.is_validated);
  }
}
